import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import DictableTextarea from '@/components/DictableTextarea';
import TrendPesoCard from '@/components/ospite/TrendPesoCard';
import { toast } from 'sonner';
import { Apple, Plus, Save, AlertTriangle, Utensils, Pill, Droplets, Printer, Ban, Trash2 } from 'lucide-react';
import { openPrintWindow } from '@/lib/letterhead';
import DatePickerField from '@/components/DatePickerField';

const PASTI = ['colazione', 'pranzo', 'merenda', 'cena', 'spuntino'];
const PERCENTUALI = [0, 25, 50, 75, 100];
const DIETE = ['libera', 'ipocalorica', 'iposodica', 'diabetica', 'ipoproteica', 'senza glutine', 'ipercalorica-iperproteica', 'personalizzata'];
const CONSISTENZE = ['normale', 'tritata', 'frullata/omogeneizzata', 'semiliquida', 'liquida addensata'];
const TIPOLOGIE = ['ONS ipercalorico-iperproteico', 'proteine in polvere', 'addensante', 'vitamine/minerali', 'ferro', 'vitamina D', 'omega 3', 'altro'];

const barColor = (v) => (v <= 25 ? 'bg-red-500' : v <= 50 ? 'bg-amber-500' : v <= 75 ? 'bg-lime-500' : 'bg-brand');

export const NutrizioneTab = ({ ospiteId, ospite }) => {
  const { hasRole } = useAuth();
  const [d, setD] = useState(null);
  const [scheda, setScheda] = useState({ tipo_dieta: '', consistenza: '', addensante: false, liquidi_obiettivo_ml: '', supervisione_pasti: '', ausili: '', obiettivo_nutrizionale: '', note: '' });
  const [pasto, setPasto] = useState({ pasto: 'pranzo', percentuale_consumata: 100, liquidi_ml: '', note: '', data: new Date().toISOString().slice(0, 10) });
  const [showIntegratore, setShowIntegratore] = useState(false);
  const [integratore, setIntegratore] = useState({ nome: '', tipologia: '', dosaggio: '', orari: '', prescritto_da: '', note: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/analisi/nutrizione/${ospiteId}`);
      setD(res.data);
      if (res.data.scheda) {
        setScheda({
          tipo_dieta: res.data.scheda.tipo_dieta || '', consistenza: res.data.scheda.consistenza || '',
          addensante: !!res.data.scheda.addensante, liquidi_obiettivo_ml: res.data.scheda.liquidi_obiettivo_ml ?? '',
          supervisione_pasti: res.data.scheda.supervisione_pasti || '', ausili: res.data.scheda.ausili || '',
          obiettivo_nutrizionale: res.data.scheda.obiettivo_nutrizionale || '', note: res.data.scheda.note || '',
        });
      }
    } catch { setD(null); }
  }, [ospiteId]);

  useEffect(() => { load(); }, [load]);

  const salvaScheda = async () => {
    setSaving(true);
    try {
      await api.put(`/analisi/nutrizione/${ospiteId}`, {
        ...scheda,
        liquidi_obiettivo_ml: scheda.liquidi_obiettivo_ml ? Number(scheda.liquidi_obiettivo_ml) : null,
      });
      toast.success('Scheda nutrizionale salvata');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setSaving(false);
  };

  const registraPasto = async () => {
    try {
      await api.post(`/analisi/nutrizione/${ospiteId}/pasti`, {
        ...pasto,
        liquidi_ml: pasto.liquidi_ml ? Number(pasto.liquidi_ml) : null,
      });
      toast.success('Pasto registrato');
      setPasto(p => ({ ...p, percentuale_consumata: 100, liquidi_ml: '', note: '' }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const salvaIntegratore = async () => {
    if (!integratore.nome.trim()) { toast.error('Indica il nome dell\'integratore'); return; }
    try {
      await api.post(`/analisi/nutrizione/${ospiteId}/integratori`, {
        ...integratore,
        orari: integratore.orari ? integratore.orari.split(',').map(s => s.trim()).filter(Boolean) : [],
      });
      toast.success('Integratore aggiunto');
      setShowIntegratore(false);
      setIntegratore({ nome: '', tipologia: '', dosaggio: '', orari: '', prescritto_da: '', note: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const sospendi = async (id) => {
    try { await api.put(`/analisi/nutrizione/integratori/${id}/sospendi`); toast.success('Integratore sospeso'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const eliminaIntegratore = async (id) => {
    try { await api.delete(`/analisi/nutrizione/integratori/${id}`); toast.success('Integratore eliminato'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const eliminaPasto = async (id) => {
    try { await api.delete(`/analisi/nutrizione/pasti/${id}`); toast.success('Registrazione eliminata'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const stampa = () => {
    if (!d) return;
    const nome = `${ospite?.cognome || ''} ${ospite?.nome || ''}`;
    let body = `<div class="info-grid">
      <div class="info-item"><span class="info-label">Ospite</span> <span class="info-value">${nome}</span></div>
      <div class="info-item"><span class="info-label">Peso attuale</span> <span class="info-value">${d.peso.peso_attuale ?? '-'} kg</span></div>
      <div class="info-item"><span class="info-label">BMI</span> <span class="info-value">${d.peso.bmi ?? '-'} ${d.peso.bmi_categoria || ''}</span></div>
      <div class="info-item"><span class="info-label">Rischio nutrizionale</span> <span class="info-value">${d.peso.rischio_livello || '-'}</span></div>
      <div class="info-item"><span class="info-label">Dieta</span> <span class="info-value">${scheda.tipo_dieta || '-'}</span></div>
      <div class="info-item"><span class="info-label">Consistenza</span> <span class="info-value">${scheda.consistenza || '-'}${scheda.addensante ? ' + addensante' : ''}</span></div>
      <div class="info-item"><span class="info-label">Obiettivo liquidi</span> <span class="info-value">${scheda.liquidi_obiettivo_ml || '-'} ml/die</span></div>
      <div class="info-item"><span class="info-label">Supervisione ai pasti</span> <span class="info-value">${scheda.supervisione_pasti || '-'}</span></div>
    </div>`;
    if (scheda.obiettivo_nutrizionale) body += `<p class="section-title">Obiettivo nutrizionale</p><p>${scheda.obiettivo_nutrizionale}</p>`;
    if (d.alert?.length) body += `<div class="alert-box alert-amber"><b>Segnalazioni</b><br/>${d.alert.join('<br/>')}</div>`;
    if (d.integratori?.length) {
      body += `<p class="section-title">Integratori</p><table><thead><tr><th>Nome</th><th>Tipologia</th><th>Dosaggio</th><th>Orari</th><th>Dal</th><th>Stato</th></tr></thead><tbody>`;
      d.integratori.forEach(i => {
        body += `<tr><td>${i.nome}</td><td>${i.tipologia || ''}</td><td>${i.dosaggio || ''}</td><td>${(i.orari || []).join(', ')}</td><td>${i.data_inizio || ''}</td><td>${i.stato}</td></tr>`;
      });
      body += `</tbody></table>`;
    }
    const r = d.diario_7_giorni;
    body += `<p class="section-title">Diario alimentare - media ultimi 7 giorni</p><table><thead><tr><th>Pasto</th><th>Consumo medio</th></tr></thead><tbody>`;
    PASTI.forEach(p => { if (r.media_per_pasto?.[p] !== undefined) body += `<tr><td>${p}</td><td>${r.media_per_pasto[p]}%</td></tr>`; });
    body += `<tr><td><b>Media globale</b></td><td><b>${r.media_globale ?? '-'}%</b></td></tr>`;
    body += `<tr><td>Idratazione media</td><td>${r.liquidi_media_die ?? '-'} ml/die</td></tr></tbody></table>`;
    if (r.pasti?.length) {
      body += `<p class="section-title">Registrazioni recenti</p><table><thead><tr><th>Data</th><th>Pasto</th><th>Consumato</th><th>Liquidi</th><th>Note</th><th>Operatore</th></tr></thead><tbody>`;
      r.pasti.slice(0, 30).forEach(p => {
        body += `<tr><td>${p.data}</td><td>${p.pasto}</td><td${p.percentuale_consumata <= 25 ? ' style="color:#dc2626;font-weight:700"' : ''}>${p.percentuale_consumata}%</td><td>${p.liquidi_ml || ''}</td><td>${p.note || ''}</td><td>${p.operatore_nome || ''}</td></tr>`;
      });
      body += `</tbody></table>`;
    }
    body += `<div class="signature-row"><div class="signature-block">Il Medico</div><div class="signature-block">Coordinatore</div></div>`;
    openPrintWindow('Scheda Nutrizionale', body, { subtitle: nome });
  };

  if (!d) return <p className="text-xs text-slate-400 py-8 text-center">Caricamento scheda nutrizionale...</p>;

  const r7 = d.diario_7_giorni;

  return (
    <div className="space-y-4" data-testid="nutrizione-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Apple className="w-4 h-4 text-brand" />Scheda Nutrizionale</h3>
        <Button size="sm" variant="outline" onClick={stampa} data-testid="nutrizione-print-btn"><Printer className="w-3.5 h-3.5 mr-1" />Stampa scheda</Button>
      </div>

      {d.alert?.length > 0 && (
        <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 text-xs" data-testid="nutrizione-alert">
          <p className="font-semibold text-amber-800 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Segnalazioni nutrizionali</p>
          <ul className="list-disc list-inside space-y-0.5 text-amber-700">{d.alert.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}

      <TrendPesoCard ospiteId={ospiteId} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-3 rounded-xl border border-slate-200 bg-white space-y-2" data-testid="nutrizione-scheda">
          <p className="text-sm font-semibold text-slate-800">Dieta e modalita di assunzione</p>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Tipo dieta</Label>
              <Select value={scheda.tipo_dieta} onValueChange={v => setScheda(s => ({ ...s, tipo_dieta: v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="nutrizione-dieta-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{DIETE.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Consistenza</Label>
              <Select value={scheda.consistenza} onValueChange={v => setScheda(s => ({ ...s, consistenza: v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="nutrizione-consistenza-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{CONSISTENZE.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Obiettivo liquidi (ml/die)</Label>
              <Input type="number" value={scheda.liquidi_obiettivo_ml} onChange={e => setScheda(s => ({ ...s, liquidi_obiettivo_ml: e.target.value }))} className="h-8 text-xs" placeholder="es. 1500" data-testid="nutrizione-liquidi-input" />
            </div>
            <div><Label className="text-xs">Supervisione ai pasti</Label>
              <Input value={scheda.supervisione_pasti} onChange={e => setScheda(s => ({ ...s, supervisione_pasti: e.target.value }))} className="h-8 text-xs" placeholder="es. imboccamento totale" data-testid="nutrizione-supervisione-input" />
            </div>
            <div className="col-span-2"><Label className="text-xs">Ausili</Label>
              <Input value={scheda.ausili} onChange={e => setScheda(s => ({ ...s, ausili: e.target.value }))} className="h-8 text-xs" placeholder="es. posate ergonomiche, bicchiere con beccuccio" />
            </div>
            <div className="col-span-2">
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={scheda.addensante} onChange={e => setScheda(s => ({ ...s, addensante: e.target.checked }))} data-testid="nutrizione-addensante-check" />
                Liquidi con addensante (rischio disfagia)
              </label>
            </div>
            <div className="col-span-2"><Label className="text-xs">Obiettivo nutrizionale</Label>
              <DictableTextarea value={scheda.obiettivo_nutrizionale} onChange={e => setScheda(s => ({ ...s, obiettivo_nutrizionale: e.target.value }))} rows={2}
                className="text-xs" contesto="Obiettivo nutrizionale" placeholder="es. recupero di 2 kg in 3 mesi, mantenimento apporto proteico" data-testid="nutrizione-obiettivo-input" />
            </div>
            <div className="col-span-2"><Label className="text-xs">Note</Label>
              <DictableTextarea value={scheda.note} onChange={e => setScheda(s => ({ ...s, note: e.target.value }))} rows={2} className="text-xs" contesto="Note nutrizionali" />
            </div>
          </div>
          {hasRole('coordinatore', 'medico', 'infermiere') && (
            <div className="flex justify-end">
              <Button size="sm" onClick={salvaScheda} disabled={saving} className="bg-brand hover:bg-brand-dark text-white" data-testid="nutrizione-salva-scheda-btn">
                <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Salvataggio...' : 'Salva scheda'}
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="p-3 rounded-xl border border-slate-200 bg-white space-y-2" data-testid="nutrizione-registra-pasto">
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Utensils className="w-4 h-4 text-brand" />Registra consumo del pasto</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Pasto</Label>
                <Select value={pasto.pasto} onValueChange={v => setPasto(p => ({ ...p, pasto: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="pasto-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PASTI.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Data</Label>
                <DatePickerField value={pasto.data} onChange={v => setPasto(p => ({ ...p, data: v }))} className="h-8 text-xs" testId="pasto-data-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Quanto ha consumato</Label>
              <div className="flex gap-1.5 mt-1">
                {PERCENTUALI.map(v => (
                  <button key={v} onClick={() => setPasto(p => ({ ...p, percentuale_consumata: v }))}
                    className={`flex-1 py-1.5 text-xs rounded-lg border transition-colors ${pasto.percentuale_consumata === v ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-brand'}`}
                    data-testid={`pasto-perc-${v}`}>{v}%</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Liquidi assunti (ml)</Label>
                <Input type="number" value={pasto.liquidi_ml} onChange={e => setPasto(p => ({ ...p, liquidi_ml: e.target.value }))} className="h-8 text-xs" placeholder="es. 200" data-testid="pasto-liquidi-input" />
              </div>
              <div><Label className="text-xs">Note</Label>
                <Input value={pasto.note} onChange={e => setPasto(p => ({ ...p, note: e.target.value }))} className="h-8 text-xs" placeholder="es. rifiuta la carne" data-testid="pasto-note-input" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={registraPasto} className="bg-brand hover:bg-brand-dark text-white" data-testid="pasto-salva-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />Registra
              </Button>
            </div>
          </div>

          <div className="p-3 rounded-xl border border-slate-200 bg-white" data-testid="nutrizione-riepilogo">
            <p className="text-sm font-semibold text-slate-800 mb-2">Consumo medio (ultimi 7 giorni)</p>
            {r7.registrazioni === 0 ? <p className="text-xs text-slate-400 py-3 text-center">Nessuna registrazione</p> : (
              <div className="space-y-1.5">
                {PASTI.filter(p => r7.media_per_pasto?.[p] !== undefined).map(p => (
                  <div key={p} className="flex items-center gap-2 text-xs">
                    <span className="w-20 text-slate-600 capitalize">{p}</span>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full ${barColor(r7.media_per_pasto[p])}`} style={{ width: `${r7.media_per_pasto[p]}%` }} />
                    </div>
                    <span className="w-10 text-right font-semibold text-slate-700">{r7.media_per_pasto[p]}%</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-xs pt-1.5 border-t border-slate-100">
                  <span className="text-slate-500">Media globale</span>
                  <span className={`font-bold ${r7.media_globale < 50 ? 'text-red-600' : r7.media_globale < 70 ? 'text-amber-600' : 'text-brand'}`} data-testid="nutrizione-media-globale">{r7.media_globale ?? '-'}%</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 flex items-center gap-1"><Droplets className="w-3 h-3 text-sky-500" />Idratazione media</span>
                  <span className="font-semibold text-slate-700">{r7.liquidi_media_die ?? '-'} ml/die</span>
                </div>
                <p className="text-[10px] text-slate-400">{r7.registrazioni} registrazioni su {r7.giorni_con_registrazioni} giorni · media 30 giorni: {d.diario_30_giorni.media_globale ?? '-'}%</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-3 rounded-xl border border-slate-200 bg-white" data-testid="nutrizione-integratori">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Pill className="w-4 h-4 text-violet-600" />Integratori ({d.integratori_attivi} attivi)</p>
          {hasRole('coordinatore', 'medico', 'infermiere') && (
            <Button size="sm" variant="outline" onClick={() => setShowIntegratore(true)} data-testid="integratore-add-btn"><Plus className="w-3.5 h-3.5 mr-1" />Aggiungi</Button>
          )}
        </div>
        {d.integratori.length === 0 ? <p className="text-xs text-slate-400 py-4 text-center">Nessun integratore prescritto</p> : (
          <div className="space-y-2">
            {d.integratori.map(i => (
              <div key={i.id} className={`p-2 rounded-lg border text-xs flex flex-wrap items-center justify-between gap-2 ${i.stato === 'attivo' ? 'bg-violet-50 border-violet-100' : 'bg-slate-50 border-slate-200 opacity-70'}`} data-testid={`integratore-${i.id}`}>
                <div>
                  <p className="font-medium text-slate-800">{i.nome} {i.dosaggio && <span className="text-slate-500 font-normal">· {i.dosaggio}</span>}</p>
                  <p className="text-slate-500">{i.tipologia || 'tipologia n/d'}{(i.orari || []).length ? ` · ${i.orari.join(', ')}` : ''} · dal {i.data_inizio}{i.prescritto_da ? ` · ${i.prescritto_da}` : ''}</p>
                  {i.note && <p className="text-slate-500">{i.note}</p>}
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className={`text-[9px] ${i.stato === 'attivo' ? 'bg-violet-100 text-violet-700' : 'bg-slate-200 text-slate-600'}`}>{i.stato}</Badge>
                  {i.stato === 'attivo' && hasRole('coordinatore', 'medico', 'infermiere') && (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-600" onClick={() => sospendi(i.id)} data-testid={`integratore-sospendi-${i.id}`}><Ban className="w-3.5 h-3.5" /></Button>
                  )}
                  {hasRole('coordinatore', 'medico') && (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => eliminaIntegratore(i.id)} data-testid={`integratore-elimina-${i.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {r7.pasti?.length > 0 && (
        <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="nutrizione-diario-tabella">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left px-2 py-1.5">Data</th><th className="px-2">Pasto</th><th className="px-2">Consumato</th><th className="px-2">Liquidi</th><th className="text-left px-2">Note</th><th className="px-2">Operatore</th><th className="px-2"></th></tr></thead>
            <tbody>
              {r7.pasti.slice(0, 20).map(p => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-2 py-1">{new Date(p.data).toLocaleDateString('it-IT')}</td>
                  <td className="px-2 text-center capitalize">{p.pasto}</td>
                  <td className={`px-2 text-center font-semibold ${p.percentuale_consumata <= 25 ? 'text-red-600' : p.percentuale_consumata <= 50 ? 'text-amber-600' : 'text-slate-700'}`}>{p.percentuale_consumata}%</td>
                  <td className="px-2 text-center text-slate-500">{p.liquidi_ml || '-'}</td>
                  <td className="px-2 text-slate-500">{p.note || ''}</td>
                  <td className="px-2 text-center text-slate-400">{p.operatore_nome}</td>
                  <td className="px-2 text-center">
                    <button onClick={() => eliminaPasto(p.id)} className="text-slate-300 hover:text-red-600" data-testid={`pasto-elimina-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showIntegratore} onOpenChange={setShowIntegratore}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo Integratore</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label className="text-xs">Nome *</Label>
              <Input value={integratore.nome} onChange={e => setIntegratore(i => ({ ...i, nome: e.target.value }))} placeholder="es. Fresubin Protein Energy" data-testid="integratore-nome-input" />
            </div>
            <div><Label className="text-xs">Tipologia</Label>
              <Select value={integratore.tipologia} onValueChange={v => setIntegratore(i => ({ ...i, tipologia: v }))}>
                <SelectTrigger data-testid="integratore-tipologia-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{TIPOLOGIE.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Dosaggio</Label>
                <Input value={integratore.dosaggio} onChange={e => setIntegratore(i => ({ ...i, dosaggio: e.target.value }))} placeholder="es. 1 flacone 200 ml" data-testid="integratore-dosaggio-input" />
              </div>
              <div><Label className="text-xs">Orari (separati da virgola)</Label>
                <Input value={integratore.orari} onChange={e => setIntegratore(i => ({ ...i, orari: e.target.value }))} placeholder="10:00, 16:00" data-testid="integratore-orari-input" />
              </div>
            </div>
            <div><Label className="text-xs">Prescritto da</Label>
              <Input value={integratore.prescritto_da} onChange={e => setIntegratore(i => ({ ...i, prescritto_da: e.target.value }))} placeholder="es. Dott. Rossi / dietista" />
            </div>
            <div><Label className="text-xs">Note</Label>
              <DictableTextarea value={integratore.note} onChange={e => setIntegratore(i => ({ ...i, note: e.target.value }))} rows={2} contesto="Note integratore nutrizionale" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowIntegratore(false)}>Annulla</Button>
              <Button onClick={salvaIntegratore} className="bg-brand hover:bg-brand-dark text-white" data-testid="integratore-salva-btn">Aggiungi</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NutrizioneTab;
